import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Form, Switch, InputNumber, Select, Divider, Row, Col, Tooltip, Spin } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import pageS from '../../s.module.less';
import s from './s.module.less';
import { BillingPolicy, BillingPolicyType } from 'types/paymentSetup';
import { useBillingPolicy } from 'hooks/paymentSetup/useBillingPolicy';
import { editBillingPolicy, editBillingPolicyByOps } from 'api/paymentSetup';

const { Option } = Select;
interface PolicyFormValues {
    noShowTimeFrame: number;
    noShowFee: number;
    lateCancellationTimeFrame: number;
    lateCancellationFee: number;
    reschedulingTimeFrame: number;
    reschedulingFee: number;
}

interface BillingPoliciesStepProps {
    isEdit?: boolean;
    isReview?: boolean;
    isOps?: boolean;
    providerId?: number;
}

const noShowTimeFrameOptions = [
    { value: 10, label: '10 minutes after the appointment start time' },
    { value: 15, label: '15 minutes after the appointment start time' },
    { value: 20, label: '20 minutes after the appointment start time' },
    { value: 30, label: '30 minutes after the appointment start time' },
];

const lateCancellationTimeFrameOptions = [
    { value: 12, label: '12 hours before the scheduled start time' },
    { value: 24, label: '24 hours before the scheduled start time' },
    { value: 48, label: '48 hours before the scheduled start time' },
    { value: 72, label: '72 hours before the scheduled start time' },
    { value: 6, label: '6 hours before the scheduled start time' },
];

const reschedulingTimeFrameOptions = [
    { value: 6, label: 'Less than 6 hours\' notice' },
    { value: 12, label: 'Less than 12 hours\' notice' },
    { value: 24, label: 'Less than 24 hours\' notice' },
    { value: 48, label: 'Less than 48 hours\' notice' },
];

const tooltips = {
    noShowTimeFrame: 'Defines how long you\'ll wait after the scheduled appointment time before considering a patient a no-show.',
    noShowFee: 'Enter the amount you\'ll charge patients who miss appointments without providing adequate notice.',
    patientFacingPolicy: 'This text will be shown to patients during booking to inform them of your policy terms.',
    reschedulingTimeFrame: 'Sets how far in advance patients must reschedule to avoid a fee.',
    lateCancellationTimeFrame: 'Define how far in advance patients must cancel their appointment to avoid a fee.',
};

const BillingPoliciesStep = forwardRef<any, BillingPoliciesStepProps>(({
    isEdit = true,
    isReview = false,
    isOps = false,
    providerId,
}, ref) => {
    const [form] = Form.useForm<PolicyFormValues>();
    const [noShowEnabled, setNoShowEnabled] = useState<boolean>(true);
    const [lateCancellationEnabled, setLateCancellationEnabled] = useState<boolean>(true);
    const [reschedulingEnabled, setReschedulingEnabled] = useState<boolean>(false);
    const [noShowPolicyText, setNoShowPolicyText] = useState<string>('');
    const [lateCancellationPolicyText, setLateCancellationPolicyText] = useState<string>('');
    const [reschedulingPolicyText, setReschedulingPolicyText] = useState<string>('');

    const [formData, loading, fetchData] = useBillingPolicy(isOps, providerId);

    const noShowTimeFrame = Form.useWatch('noShowTimeFrame', form);
    const noShowFee = Form.useWatch('noShowFee', form);
    const lateCancellationTimeFrame = Form.useWatch('lateCancellationTimeFrame', form);
    const lateCancellationFee = Form.useWatch('lateCancellationFee', form);
    const reschedulingTimeFrame = Form.useWatch('reschedulingTimeFrame', form);
    const reschedulingFee = Form.useWatch('reschedulingFee', form);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        const values = {
            noShowTimeFrame: formData.find((item) => item.type === BillingPolicyType.NO_SHOW)?.timeFrame || 15,
            noShowFee: formData.find((item) => item.type === BillingPolicyType.NO_SHOW)?.fee || 25,
            lateCancellationTimeFrame: formData.find((item) => item.type === BillingPolicyType.LATE_CANCELLATION)?.timeFrame || 24,
            lateCancellationFee: formData.find((item) => item.type === BillingPolicyType.LATE_CANCELLATION)?.fee || 25,
            reschedulingTimeFrame: formData.find((item) => item.type === BillingPolicyType.RESCHEDULE)?.timeFrame || 24,
            reschedulingFee: formData.find((item) => item.type === BillingPolicyType.RESCHEDULE)?.fee || 25,
        };
        form.setFieldsValue(values);
        setNoShowEnabled(formData.find((item) => item.type === BillingPolicyType.NO_SHOW)?.enable ?? true);
        setLateCancellationEnabled(formData.find((item) => item.type === BillingPolicyType.LATE_CANCELLATION)?.enable ?? true);
        setReschedulingEnabled(formData.find((item) => item.type === BillingPolicyType.RESCHEDULE)?.enable ?? false);
    }, [formData]);

    useEffect(() => {
        setNoShowPolicyText(`I agree that if I do not attend my scheduled appointment within ${noShowTimeFrameOptions.find((option) => option.value === noShowTimeFrame)?.label}, a no-show fee of $${noShowFee ?? 0} may apply.`);
    }, [noShowTimeFrame, noShowFee]);

    useEffect(() => {
        setLateCancellationPolicyText(`I agree that if I need to cancel my appointment with less than ${lateCancellationTimeFrameOptions.find((option) => option.value === lateCancellationTimeFrame)?.label} notice, a late cancellation fee of $${lateCancellationFee ?? 0} may apply.`);
    }, [lateCancellationTimeFrame, lateCancellationFee]);

    useEffect(() => {
        setReschedulingPolicyText(`I understand that if I reschedule my appointment with ${reschedulingTimeFrameOptions.find((option) => option.value === reschedulingTimeFrame)?.label}, a rescheduling fee of $${reschedulingFee ?? 0} may apply.`);
    }, [reschedulingTimeFrame, reschedulingFee]);

    const handleStepSubmit = async () => {
        try {
            await form.validateFields();
            const values = form.getFieldsValue();
            const params: BillingPolicy[] = [];
            if (noShowEnabled) {
                params.push({
                    type: BillingPolicyType.NO_SHOW,
                    timeFrame: values.noShowTimeFrame,
                    fee: values.noShowFee,
                });
            }
            if (lateCancellationEnabled) {
                params.push({
                    type: BillingPolicyType.LATE_CANCELLATION,
                    timeFrame: values.lateCancellationTimeFrame,
                    fee: values.lateCancellationFee,
                });
            }
            if (reschedulingEnabled) {
                params.push({
                    type: BillingPolicyType.RESCHEDULE,
                    timeFrame: values.reschedulingTimeFrame,
                    fee: values.reschedulingFee,
                });
            }

            const api = isOps ? editBillingPolicyByOps(params, providerId!) : editBillingPolicy(params);
            await api;
            await fetchData();
        } catch (errorInfo) {
            console.error('Billing Policies validation/save failed:', errorInfo);
            throw errorInfo;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit: handleStepSubmit,
    }));

    if (!isEdit) {
        const noShowTimeFrameLabel = noShowTimeFrameOptions.find((option) => option.value === form.getFieldValue('noShowTimeFrame'))?.label;
        const lateCancelTimeFrameLabel = lateCancellationTimeFrameOptions.find((option) => option.value === form.getFieldValue('lateCancellationTimeFrame'))?.label;
        const reschedulingTimeFrameLabel = reschedulingTimeFrameOptions.find((option) => option.value === form.getFieldValue('reschedulingTimeFrame'))?.label;
        const noShowFeeValue = form.getFieldValue('noShowFee');
        const lateCancellationFeeValue = form.getFieldValue('lateCancellationFee');
        const reschedulingFeeValue = form.getFieldValue('reschedulingFee');

        return (
            <div className={s.summaryContent}>
                {noShowEnabled && (
                    <div className={s.policySummarySection}>
                        <h3 className={s.summaryTitle}>No-show fee policy</h3>
                        <Row gutter={[24, 16]} className={s.summaryRow}>
                            <Col span={12}>
                                <div className={s.summaryItem}>
                                    <div className={s.summaryLabel}>No-show timeframe</div>
                                    <div className={s.summaryValue}>{noShowTimeFrameLabel}</div>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div className={s.summaryItem}>
                                    <div className={s.summaryLabel}>No-show fee</div>
                                    <div className={s.summaryValue}>{noShowFeeValue}</div>
                                </div>
                            </Col>
                            <Col span={24}>
                                <div className={s.summaryItem}>
                                    <div className={s.summaryLabel}>Patient-facing policy terms</div>
                                    <div className={s.summaryValue}>{`I agree that if I do not attend my scheduled appointment within ${noShowTimeFrameLabel} start time, a no-show fee of $${noShowFeeValue} may apply.`}</div>
                                </div>
                            </Col>
                        </Row>

                    </div>
                )}

                {lateCancellationEnabled && (
                    <>
                        <Divider />
                        <div className={s.policySummarySection}>
                            <h3 className={s.summaryTitle}>Late cancellation policy</h3>
                            <Row gutter={[24, 16]} className={s.summaryRow}>
                                <Col span={12}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Late cancellation timeframe</div>
                                        <div className={s.summaryValue}>{lateCancelTimeFrameLabel}</div>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Late cancellation fee</div>
                                        <div className={s.summaryValue}>{lateCancellationFeeValue}</div>
                                    </div>
                                </Col>
                                <Col span={24}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Patient-facing policy terms</div>
                                        <div className={s.summaryValue}>{`I agree that if I need to cancel my appointment with less than ${lateCancelTimeFrameLabel} notice, a late cancellation fee of $${lateCancellationFeeValue} may apply.`}</div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}

                {reschedulingEnabled && (
                    <>
                        <Divider />
                        <div className={s.policySummarySection}>
                            <h3 className={s.summaryTitle}>Rescheduling policy</h3>
                            <Row gutter={[24, 16]} className={s.summaryRow}>
                                <Col span={12}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Rescheduling timeframe</div>
                                        <div className={s.summaryValue}>{reschedulingTimeFrameLabel}</div>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Rescheduling fee</div>
                                        <div className={s.summaryValue}>{reschedulingFeeValue}</div>
                                    </div>
                                </Col>
                                <Col span={24}>
                                    <div className={s.summaryItem}>
                                        <div className={s.summaryLabel}>Patient-facing policy terms</div>
                                        <div className={s.summaryValue}>{`I understand that if I reschedule my appointment with ${reschedulingTimeFrameLabel}, a rescheduling fee of $${reschedulingFeeValue} may apply.`}</div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}

                {!noShowEnabled && !lateCancellationEnabled && !reschedulingEnabled && (
                    <p>No billing policies are currently enabled.</p>
                )}
            </div>
        );
    }

    return (
        <div className={s.billingPoliciesStep}>
            {!isReview ? (
                <div className={s.header}>
                    <h2 className={pageS.stepTitle}>Appointment billing policies</h2>
                    <p className={pageS.desc}>Set up your no-show, late cancellation, and rescheduling policies to protect your time and reduce missed appointments.</p>
                </div>
            ) : null}

            <div className={s.tips}>
                <div className={s.tipIcon} />
                <div className={s.tipContent}>
                    <div className={s.tipDesc}>We’ve found that patients typically choose providers with reasonable fees. You're in a marketplace where patients compare dozens of options before booking. To stay competitive, we recommend keeping your patient penalty fee below $25.</div>
                </div>
            </div>

            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={formData}
                    onFinish={handleStepSubmit}
                >
                    <div className={s.policySection}>
                        <div className={s.policyHeader}>
                            <div>
                                <h3 className={pageS.title}>No-show fee policy</h3>
                                <p className={pageS.desc}>Charge patients who miss their scheduled appointments.</p>
                            </div>
                            <Switch
                                checked={noShowEnabled}
                                onChange={(checked) => {
                                    setNoShowEnabled(checked);
                                }}
                            />

                        </div>

                        {noShowEnabled && (
                            <div className={pageS.card}>
                                <div className={s.policyControls}>
                                    <div className={s.timeFrameSelect}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    No-show timeframe{' '}
                                                    <Tooltip title={tooltips.noShowTimeFrame}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="noShowTimeFrame"
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                optionLabelProp="label"
                                            >
                                                {noShowTimeFrameOptions.map((option) => (
                                                    <Option key={option.value} value={option.value} label={option.label}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div className={s.feeInput}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    No-show fee{' '}
                                                    <Tooltip title={tooltips.noShowFee}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="noShowFee"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (!noShowEnabled) {
                                                            return Promise.resolve();
                                                        }
                                                        if (value !== null && value !== undefined) {
                                                            if (value <= 0) {
                                                                return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                            }
                                                            if (value > 200) {
                                                                return Promise.reject(new Error('Amount must be less than or equal to $100.00'));
                                                            }
                                                        } else {
                                                            return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                addonBefore="$"
                                                min={0}
                                                max={200}
                                                precision={2}
                                                controls={false}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div>
                                    <span className="mr-1">Patient-facing policy terms</span>
                                    <Tooltip title={tooltips.patientFacingPolicy}>
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                    <p className="mt-1">{noShowPolicyText}</p>
                                </div>

                            </div>
                        )}
                    </div>

                    <Divider />

                    <div className={s.policySection}>
                        <div className={s.policyHeader}>
                            <div>
                                <h3 className={pageS.title}>Late cancellation policy</h3>
                                <p className={pageS.desc}>Establish fees for appointments cancelled with insufficient advance notice.</p>
                            </div>
                            <Switch
                                checked={lateCancellationEnabled}
                                onChange={(checked) => {
                                    setLateCancellationEnabled(checked);
                                }}
                            />
                        </div>
                        <div style={{ marginTop: '16px' }} className={s.tips}>
                            <div className={s.tipIcon} />
                            <div className={s.tipContent}>
                                <div className={s.tipDesc}>Klarity Health allows patients to cancel appointments for free within 24 hours of booking. If a patient cancels within this window, you will not be able to charge a late cancellation fee. Please take this into account when setting your policy.</div>
                            </div>
                        </div>

                        {lateCancellationEnabled && (
                            <div className={pageS.card}>
                                <div className={s.policyControls}>
                                    <div className={s.timeFrameSelect}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    Late cancellation timeframe{' '}
                                                    <Tooltip title={tooltips.lateCancellationTimeFrame}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="lateCancellationTimeFrame"
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                optionLabelProp="label"
                                            >
                                                {lateCancellationTimeFrameOptions.map((option) => (
                                                    <Option key={option.value} value={option.value} label={option.label}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div className={s.feeInput}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    Late cancellation fee{' '}
                                                    <Tooltip title={tooltips.noShowFee}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="lateCancellationFee"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (!lateCancellationEnabled) {
                                                            return Promise.resolve();
                                                        }
                                                        if (value !== null && value !== undefined) {
                                                            if (value <= 0) {
                                                                return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                            }
                                                            if (value > 200) {
                                                                return Promise.reject(new Error('Amount must be less than or equal to $100.00'));
                                                            }
                                                        } else {
                                                            return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                addonBefore="$"
                                                min={0}
                                                max={200}
                                                precision={2}
                                                controls={false}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div>
                                    <span className="mr-1">Patient-facing policy terms</span>
                                    <Tooltip title={tooltips.patientFacingPolicy}>
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                    <p className="mt-1">{lateCancellationPolicyText}</p>
                                </div>
                            </div>
                        )}
                    </div>

                    <Divider />

                    <div className={s.policySection}>
                        <div className={s.policyHeader}>
                            <div>
                                <h3 className={pageS.title}>Rescheduling policy</h3>
                                <p className={pageS.desc}>Define guidelines for patients who need to change their appointment times and any associated fees.</p>
                            </div>
                            <Switch
                                checked={reschedulingEnabled}
                                onChange={(checked) => {
                                    setReschedulingEnabled(checked);
                                }}
                            />
                        </div>

                        {reschedulingEnabled && (
                            <div className={pageS.card}>
                                <div className={s.policyControls}>
                                    <div className={s.timeFrameSelect}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    Rescheduling timeframe{' '}
                                                    <Tooltip title={tooltips.reschedulingTimeFrame}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="reschedulingTimeFrame"
                                        >
                                            <Select
                                                style={{ width: '100%' }}
                                                dropdownMatchSelectWidth={false}
                                                optionLabelProp="label"
                                            >
                                                {reschedulingTimeFrameOptions.map((option) => (
                                                    <Option key={option.value} value={option.value} label={option.label}>
                                                        {option.label}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>

                                    <div className={s.feeInput}>
                                        <Form.Item
                                            label={
                                                <span>
                                                    Rescheduling fee{' '}
                                                    <Tooltip title={tooltips.noShowFee}>
                                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                                    </Tooltip>
                                                </span>
                                            }
                                            name="reschedulingFee"
                                            rules={[
                                                {
                                                    validator: (_, value) => {
                                                        if (!reschedulingEnabled) {
                                                            return Promise.resolve();
                                                        }
                                                        if (value !== null && value !== undefined) {
                                                            if (value <= 0) {
                                                                return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                            }
                                                            if (value > 200) {
                                                                return Promise.reject(new Error('Amount must be less than or equal to $100.00'));
                                                            }
                                                        } else {
                                                            return Promise.reject(new Error('Amount must be greater than $0.00'));
                                                        }
                                                        return Promise.resolve();
                                                    },
                                                },
                                            ]}
                                        >
                                            <InputNumber
                                                addonBefore="$"
                                                min={0}
                                                max={200}
                                                precision={2}
                                                controls={false}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>

                                <div>
                                    <span className="mr-1">Patient-facing policy terms</span>
                                    <Tooltip title={tooltips.patientFacingPolicy}>
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                    <p className="mt-1">{reschedulingPolicyText}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </Form>
            </Spin>
        </div>
    );
});

export default BillingPoliciesStep;
